import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FolderKanban, Users, Calendar, ArrowLeft, Building2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format date from YYYY-MM-DD to DD/MM/YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const [year, month, day] = dateStr.split('-');
    if (year && month && day) {
      return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
    }
    return dateStr;
  } catch {
    return dateStr;
  }
};

export default function PublicGTLDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [gtl, setGtl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGTLDetail();
  }, [id]);

  const fetchGTLDetail = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/public/gtl/${id}`);
      setGtl(response.data);
    } catch (error) {
      console.error('Error fetching GTL detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatoBadge = (stato) => {
    switch (stato) {
      case 'attivo':
        return <span className="badge-success">Attivo</span>;
      case 'concluso':
        return <span className="badge-neutral">Concluso</span>;
      case 'sospeso':
        return <span className="badge-warning">Sospeso</span>;
      default:
        return <span className="badge-neutral">{stato}</span>;
    }
  };

  const getRuoloBadge = (ruolo) => {
    switch (ruolo) {
      case 'Coordinatore':
        return <span className="badge-success">Coordinatore</span>;
      case 'Referente':
        return <span className="bg-blue-100 text-blue-700 border border-blue-200 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold">Referente</span>;
      case 'Membro Speciale':
        return <span className="bg-purple-100 text-purple-700 border border-purple-200 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold">Membro Speciale</span>;
      default:
        return <span className="badge-neutral">Membro</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!gtl) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500 mb-4">GTL non trovato</p>
          <Button onClick={() => navigate('/gtl-pubblico')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna alla lista
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <img src="/logo-c3i.png" alt="C3i Logo" className="w-12 h-12 object-contain" />
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Dettaglio GTL</h1>
              <p className="text-slate-500 text-sm">Informazioni sul Gruppo di Lavoro</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/gtl-pubblico')}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Torna alla lista
        </Button>

        {/* GTL Info Card */}
        <Card className="card-institutional mb-6" data-testid="gtl-info-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FolderKanban className="w-6 h-6 text-blue-700" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <CardTitle className="font-heading text-xl">{gtl.nome}</CardTitle>
                  {getStatoBadge(gtl.stato)}
                </div>
                <p className="text-slate-600 mt-1">{gtl.tema}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {gtl.descrizione && (
              <div className="p-4 bg-slate-50 rounded-lg mb-4">
                <p className="text-slate-700">{gtl.descrizione}</p>
              </div>
            )}
            <div className="flex flex-wrap gap-6 text-sm text-slate-600">
              {gtl.data_inizio && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>Inizio: {formatDate(gtl.data_inizio)}</span>
                </div>
              )}
              {gtl.data_fine && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>Fine: {formatDate(gtl.data_fine)}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span>{gtl.membri_count} membri</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Members Card */}
        <Card className="card-institutional" data-testid="membri-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-slate-600" />
              <CardTitle className="font-heading text-lg">Componenti del Gruppo</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {gtl.membri && gtl.membri.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="membri-table">
                  <thead>
                    <tr>
                      <th>Nome e Cognome</th>
                      <th>Ruolo</th>
                      <th>Ordine di Appartenenza</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gtl.membri.map((membro, index) => (
                      <tr key={index}>
                        <td>
                          <span className="font-medium">{membro.nome} {membro.cognome}</span>
                        </td>
                        <td>{getRuoloBadge(membro.ruolo)}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            <span>{membro.ordine}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                Nessun componente registrato
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-slate-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-sm text-slate-500">
            © 2026 C3i - Comitato Italiano Ingegneria dell'Informazione
          </p>
          <p className="text-center text-xs text-slate-400">
            Tutti i diritti riservati
          </p>
        </div>
      </div>
    </div>
  );
}
